/**
 * @file jobs.js
 * @description Frontend helper for the async job pattern.
 *
 * Pattern:
 *   1. POST /api/jobs/{kind} with the original payload → get {job_id}.
 *   2. Poll GET /api/jobs/{job_id} every 2.5 s until status is 'done' or 'error'.
 *   3. Resolve with the final result, or throw with the error message.
 *
 * Total time budget: 3 minutes (matches the backend runner ceiling).
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_POLL_MS = 2500;
const DEFAULT_TIMEOUT_MS = 180_000; // 3 min

/**
 * Run a long-running LLM job end-to-end. Returns a promise that resolves with
 * the runner's result object, or rejects with an Error whose .message is a
 * human-readable failure reason.
 *
 * @param {"wikifi"|"chat"|"create"} kind
 * @param {object} payload  Same body you'd send to the old sync endpoint.
 * @param {{ onProgress?: (elapsed_s: number) => void, signal?: AbortSignal, pollMs?: number, timeoutMs?: number }} [opts]
 * @returns {Promise<object>}
 */
export async function runJob(kind, payload, opts = {}) {
  const { onProgress, signal, pollMs = DEFAULT_POLL_MS, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;

  // 1) Kick off — this call is fast (<100ms) and immune to gateway timeouts.
  let start;
  try {
    start = await axios.post(`${API}/jobs/${kind}`, payload, { withCredentials: true });
  } catch (e) {
    // If the kickoff itself failed (auth, quota, malformed body), surface it.
    const msg = e?.response?.data?.detail || e?.message || "Failed to start generation";
    throw new Error(msg);
  }

  const jobId = start.data?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");

  const t0 = Date.now();

  // 2) Poll for completion.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal?.aborted) throw new Error("cancelled");
    if (Date.now() - t0 > timeoutMs) {
      throw new Error("Generation is taking longer than expected. Try again with a shorter prompt.");
    }

    if (onProgress) {
      try { onProgress(Math.floor((Date.now() - t0) / 1000)); } catch (_) {}
    }

    let poll;
    try {
      poll = await axios.get(`${API}/jobs/${jobId}`, { withCredentials: true });
    } catch (e) {
      // Transient network errors — sleep + retry rather than fail immediately.
      await sleep(pollMs);
      continue;
    }

    const status = poll.data?.status;
    if (status === "done") return poll.data.result;
    if (status === "error") throw new Error(poll.data?.error || "Generation failed");
    // queued or running → keep polling.
    await sleep(pollMs);
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
